import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { Box, Typography, Icon, Chip } from '@glplus/glpweb-dsl';
import { SIDEBAR_SECTIONS, type AdminTool } from '../lib/adminTools';
import { useCapabilities } from '../lib/useCapabilities';

/**
 * Left "More tools" rail — the secondary tool navigation, grouped by section
 * (Catalog / Delivery / Operations). Built from DSL primitives (Box /
 * Typography / Icon / Chip), painted black to match the admin chrome.
 *
 * Collapsible: the hamburger toggles between the full labelled rail and an
 * icon-only rail (the Figma ships both states). Live tools are links; `soon`
 * tools render greyed with a "Soon" chip and are non-interactive.
 */

const RAIL_BG = '#000000';
const EXPANDED = 276;
const COLLAPSED = 64;

export default function SideRail() {
  const { pathname } = useLocation();
  const caps = useCapabilities();
  const [collapsed, setCollapsed] = useState(false);

  const isActive = (to?: string) =>
    !!to && (pathname === to || pathname.startsWith(`${to}/`));

  // A tool appears only if the caller's per-app access grants its capability.
  // Sections with no visible tools are hidden entirely (server re-checks each request).
  const sections = SIDEBAR_SECTIONS.map((s) => ({
    ...s,
    tools: s.tools.filter((t) => caps.has(t.capability)),
  })).filter((s) => s.tools.length > 0);

  return (
    <Box
      component="aside"
      aria-label="More tools"
      sx={{
        width: collapsed ? COLLAPSED : EXPANDED,
        flexShrink: 0,
        bgcolor: RAIL_BG,
        color: '#fff',
        position: 'sticky',
        top: 64,
        alignSelf: 'flex-start',
        height: 'calc(100vh - 64px)',
        overflowY: 'auto',
        transition: 'width 0.18s ease',
        py: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {/* Header row: hamburger toggle + "More tools" */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: collapsed ? 0 : 2,
          justifyContent: collapsed ? 'center' : 'flex-start',
          mb: 1,
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            background: 'transparent',
            color: '#fff',
            cursor: 'pointer',
            p: 0.5,
            borderRadius: 1,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
            '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 2 },
          }}
        >
          <Icon name="hamburger" size={22} color="white" ariaLabel="" />
        </Box>
        {!collapsed && (
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.55)',
            }}
          >
            More tools
          </Typography>
        )}
      </Box>

      {sections.map((section) => (
        <Box key={section.key} sx={{ mb: 1.5 }}>
          {!collapsed && (
            <Typography
              sx={{
                px: 2,
                mb: 0.5,
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.4)',
              }}
            >
              {section.label}
            </Typography>
          )}
          {section.tools.map((tool) => (
            <RailItem key={tool.key} tool={tool} collapsed={collapsed} active={isActive(tool.to)} />
          ))}
        </Box>
      ))}
    </Box>
  );
}

function RailItem({
  tool,
  collapsed,
  active,
}: {
  tool: AdminTool;
  collapsed: boolean;
  active: boolean;
}) {
  const soon = tool.status === 'soon';

  const rowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: collapsed ? 0 : 2,
    py: 1,
    justifyContent: collapsed ? 'center' : 'flex-start',
    textDecoration: 'none',
    color: soon ? 'rgba(255,255,255,0.55)' : active ? '#fff' : 'rgba(255,255,255,0.92)',
    borderLeft: active ? '3px solid #fff' : '3px solid transparent',
    cursor: soon ? 'default' : 'pointer',
    '&:hover': soon ? {} : { bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' },
  } as const;

  const content = (
    <>
      <Icon
        name={tool.icon}
        size={20}
        color={soon ? 'rgba(255,255,255,0.55)' : 'white'}
        ariaLabel=""
      />
      {!collapsed && (
        <>
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              // Set the color explicitly here — the DSL Typography applies its
              // own default color, so inheriting from the row's color doesn't
              // work. Live links are pure white; soon items stay dimmed.
              color: soon ? 'rgba(255,255,255,0.6)' : '#ffffff',
              fontSize: '14px',
              fontWeight: active ? 700 : 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {tool.sidebarLabel ?? tool.label}
          </Typography>
          {soon && (
            <Chip
              label="Soon"
              size="small"
              backgroundColor="rgba(255,255,255,0.18)"
              textColor="rgba(255,255,255,0.85)"
              fontSize={10}
              sx={{ flexShrink: 0 }}
            />
          )}
        </>
      )}
    </>
  );

  const label = tool.sidebarLabel ?? tool.label;
  if (soon || !tool.to) {
    return (
      <Box title={collapsed ? `${label} (Soon)` : undefined} sx={rowSx}>
        {content}
      </Box>
    );
  }

  return (
    <Box component={Link} to={tool.to} title={collapsed ? label : undefined} sx={rowSx}>
      {content}
    </Box>
  );
}
